import { useContext, useEffect } from 'react'
import type { MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { MODALS_ID } from '../../constants'

import { maskingEmail, maskingName, toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useUserInfo } from '../../hooks/useUserInfo'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { Card } from '../../components/card/Card'
import { Text } from '../../components/text'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { IsSignInModal } from '../../components/modals/IsSignInModal'
import { SettingListContainer } from './components/SettingListContainer'
import { InvestSuitabilityTestModal } from '../myPage/modals/InvestSuitabilityTestModal'
import type { ListItem } from './components/SettingListContainer'
// import { OpenSourceModal } from './modal/OpenSourceModal'

import { ReactComponent as DisclosureIcon } from '../../assets/ico/more/disclosure.svg'
import { ReactComponent as EventIcon } from '../../assets/ico/more/event.svg'
import { ReactComponent as NewsIcon } from '../../assets/ico/more/news.svg'
import { ReactComponent as FaqIcon } from '../../assets/ico/more/faq.svg'
import { ReactComponent as InquiryIcon } from '../../assets/ico/more/inquiry.svg'
import { ReactComponent as NotificationIcon } from '../../assets/ico/more/notification.svg'
import { ReactComponent as NotificationSettingIcon } from '../../assets/ico/more/notificationSetting.svg'
// import { ReactComponent as PolicyIcon } from '../../assets/ico/more/policy.svg'
import { ReactComponent as TestIcon } from '../../assets/ico/more/test.svg'
import { ReactComponent as VersionIcon } from '../../assets/ico/more/version.svg'
// import { ReactComponent as OpenSourceIcon } from '../../assets/ico/more/opensource.svg'
import { ReactComponent as ArrowGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray-06_medium.svg'
import AppleIcon from '../../assets/logo/sns/apple/apple_3@3x.png'
import GoogleIcon from '../../assets/logo/sns/google/google_3@3x.png'
import KakaoIcon from '../../assets/logo/sns/kakao/kakao_3@3x.png'
import NaverIcon from '../../assets/logo/sns/naver/naver_3@3x.png'
import Yes24Icon from '../../assets/logo/sns/yes24/yes24_3@3x.png'

const HEAD_INFO = {
  title: '더 보기 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '더 보기 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}
// TODO: 최신 app version 기입 후 store의 버전과 확인
const IS_UPDATED = true

export function Setting() {
  const {
    commonStore: { appVersion, setShowTab, setActiveTab },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const navigate = useNavigate()
  const { data: userInfo, isLoading } = useUserInfo()

  const openInvestTestModal = () => {
    openModal({
      id: MODALS_ID.INVEST_SUITABILITY,
      Component: () => (
        <InvestSuitabilityTestModal
          onDismiss={() => {
            closeModal(MODALS_ID.INVEST_SUITABILITY)
          }}
        />
      ),
    })
  }

  // const openOpenSourceModal = () => {
  //   openModal({
  //     id: MODALS_ID.OPEN_SOURCE,
  //     Component: OpenSourceModal,
  //   })
  // }

  const SETTING_DATA: ListItem[] = [
    {
      title: '소식',
      list: [
        {
          title: '공시',
          icon: <DisclosureIcon />,
          rightContent: <ArrowGrayIcon />,
          link: '/disclosure',
        },
        {
          title: '공지사항',
          icon: <NotificationIcon />,
          rightContent: <ArrowGrayIcon />,
          link: '/notice',
        },
        {
          title: '이벤트',
          icon: <EventIcon />,
          rightContent: <ArrowGrayIcon />,
          link: '/event',
        },
        {
          title: '뉴스',
          icon: <NewsIcon />,
          rightContent: <ArrowGrayIcon />,
          link: '/news',
        },
      ],
    },
    {
      title: '고객지원',
      list: [
        {
          title: '1:1문의',
          icon: <InquiryIcon />,
          rightContent: <ArrowGrayIcon />,
          onClick: () => {
            if (isLoading) return
            navigate('/qna')
          },
        },
        {
          title: '자주 묻는 질문',
          icon: <FaqIcon />,
          rightContent: <ArrowGrayIcon />,
          link: '/faq',
        },
      ],
    },
    {
      title: '기타',
      list: [
        {
          title: '알림설정',
          icon: <NotificationSettingIcon />,
          rightContent: <ArrowGrayIcon />,
          onClick: () => {
            if (isLoading) return
            navigate('/push')
          },
        },
        appVersion
          ? {
              title: '버전 정보',
              icon: <VersionIcon />,
              rightContent: IS_UPDATED ? (
                <Text size={14} color="text_secondary" style={{ paddingRight: '8px' }}>
                  {appVersion}
                </Text>
              ) : (
                <Text
                  size={14}
                  color="primary_5"
                  style={{ paddingRight: '8px' }}
                  onClick={() => alert('업데이트를 진행합니다.')}
                >
                  업데이트 필요
                </Text>
              ),
            }
          : undefined,
        {
          title: '투자 적합성 테스트',
          icon: <TestIcon />,
          rightContent:
            userInfo?.suitableUntil && new Date(String(userInfo?.suitableUntil)) > new Date() ? (
              <Text size={14} color="text_secondary" style={{ paddingRight: '8px' }}>
                만료 {dayjs(userInfo?.suitableUntil).format('YYYY.MM.DD')}
              </Text>
            ) : isLoading ? (
              <Spinner />
            ) : (
              <ArrowGrayIcon />
            ),
          onClick: () => {
            if (isLoading) return
            if (!userInfo) {
              openModal({ id: MODALS_ID.IS_SIGNIN, Component: IsSignInModal })

              return
            }

            if (userInfo?.suitableUntil && new Date(String(userInfo?.suitableUntil)) > new Date()) {
              toast(
                `이미 투자 적합성 테스트를 완료했어요. (만료일: ${dayjs(userInfo?.suitableUntil).format('YYYY.MM.DD')})`,
              )
            }

            if (!userInfo?.suitableUntil || new Date(String(userInfo?.suitableUntil)) < new Date()) {
              openInvestTestModal()
            }
          },
        },
        // {
        //   title: '오픈 소스 라이브러리',
        //   icon: <OpenSourceIcon />,
        //   rightContent: <ArrowGrayIcon />,
        //   onClick: openOpenSourceModal,
        // },
      ],
    },
  ]

  useEffect(() => {
    setShowTab(true)
    setActiveTab('setting')
  }, [])

  const disabledLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isLoading) {
      e.preventDefault()
    }
  }

  const SnsIcon = () => {
    switch (userInfo?.auth.provider) {
      case 'google': {
        return <SnsLogoImg src={GoogleIcon} alt="google_icon" />
      }
      case 'kakao': {
        return <SnsLogoImg src={KakaoIcon} alt="kakao_icon" />
      }
      case 'naver': {
        return <SnsLogoImg src={NaverIcon} alt="naver_icon" />
      }
      case 'yes24': {
        return <SnsLogoImg src={Yes24Icon} alt="yes24_icon" />
      }
      case 'apple': {
        return <SnsLogoImg src={AppleIcon} alt="apple_icon" />
      }
      default: {
        return <SnsLogoImg src={Yes24Icon} alt="yes24_icon" />
      }
    }
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="더 보기" isBack={false} />
        <Content>
          <Container>
            <UserInfoBox>
              <Text size={14} lineHeight={22} color="text_secondary" block={true}>
                내정보
              </Text>
              <Link to={userInfo ? '/personalInfo' : '/signIn'} onClick={disabledLink}>
                <Card
                  $padding="16px"
                  $border={true}
                  $borderWidth={1}
                  $borderColor="primary_2"
                  $background="primary_1"
                  $radius={8}
                >
                  <RowBetween>
                    {isLoading ? (
                      <Spinner />
                    ) : userInfo ? (
                      <AlignCenterItem $gap={16}>
                        <AlignCenterItem $gap={6}>
                          <SnsIcon />
                          <Text size={14} lineHeight={22} color="text_secondary">
                            {userInfo.auth.provider}
                          </Text>
                        </AlignCenterItem>
                        <Text size={14} weight={600}>
                          {maskingEmail(userInfo?.email) || maskingName(userInfo?.name) || '본인인증이 필요해요'}
                        </Text>
                      </AlignCenterItem>
                    ) : (
                      <Text size={14} weight={600} color="primary_5">
                        로그인 후 이용이 가능해요
                      </Text>
                    )}
                    <ArrowGrayIcon />
                  </RowBetween>
                </Card>
              </Link>
            </UserInfoBox>
            <SettingListContainer items={SETTING_DATA} />
          </Container>
        </Content>
      </Wrap>
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px 0;
`

const UserInfoBox = styled(ColumnItem)`
  gap: 8px;
  margin-bottom: 16px;
  ${RowBetween} {
    width: 100%;
  }
`

const SnsLogoImg = styled.img`
  width: 14px;
  height: 14px;
`
